/**
 * Grant or revoke beta publish credits for a user. One modal, two modes —
 * mirrors RefundPaymentModal (react-hook-form + zod + ConfirmModal). Delegates
 * to the entitlement mutations, which invalidate the entitlement summary +
 * publish history on success.
 */
import { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ConfirmModal } from './ConfirmModal';
import { creditsSchema, type CreditsInput } from '../schemas';
import { useGrantCredits, useRevokeCredits } from '../hooks';

interface Props {
  open: boolean;
  mode: 'grant' | 'revoke';
  userId: string;
  /** Current balance, shown for context and to bound a revoke. */
  currentCredits: number;
  onClose: () => void;
}

export function GrantRevokeCreditsModal({ open, mode, userId, currentCredits, onClose }: Props) {
  const isGrant = mode === 'grant';
  // Hooks must be called unconditionally; pick the active mutation by mode.
  const grant = useGrantCredits(userId);
  const revoke = useRevokeCredits(userId);
  const mutation = isGrant ? grant : revoke;

  const form = useForm<CreditsInput>({
    resolver: zodResolver(creditsSchema),
    defaultValues: { amount: 1, reason: '' },
  });

  // One key per intended grant: a double-click or a retry after an error reuses
  // it (so the backend grants once); a new key only after a grant succeeds.
  const grantKey = useRef(crypto.randomUUID());

  const submit = form.handleSubmit(async (values) => {
    try {
      await mutation.mutateAsync({
        amount: values.amount,
        reason: values.reason || undefined,
        ...(isGrant ? { idempotencyKey: grantKey.current } : {}),
      });
    } catch {
      // Shown below via `mutation.error`; keep the modal (and the key) for a retry.
      return;
    }
    if (isGrant) grantKey.current = crypto.randomUUID();
    form.reset();
    onClose();
  });

  return (
    <ConfirmModal
      open={open}
      title={isGrant ? 'Grant publish credits' : 'Revoke publish credits'}
      description={`Current balance: ${currentCredits} ${currentCredits === 1 ? 'credit' : 'credits'}`}
      confirmLabel={isGrant ? 'Grant' : 'Revoke'}
      destructive={!isGrant}
      loading={mutation.isPending}
      onClose={onClose}
      onConfirm={() => void submit()}
    >
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="block text-[10px] uppercase tracking-widest font-bold text-on-surface-variant mb-1">
            Credits to {isGrant ? 'grant' : 'revoke'}
          </label>
          <input
            type="number"
            min={1}
            {...form.register('amount', { valueAsNumber: true })}
            className="w-full px-3 py-2 rounded-lg bg-surface-container-lowest border border-border-subtle text-sm focus:outline-none focus:ring-2 focus:ring-electric-violet/30"
          />
          {form.formState.errors.amount && (
            <p className="text-xs text-red-300 mt-1">{form.formState.errors.amount.message}</p>
          )}
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest font-bold text-on-surface-variant mb-1">
            Reason (optional)
          </label>
          <textarea
            {...form.register('reason')}
            rows={2}
            className="w-full px-3 py-2 rounded-lg bg-surface-container-lowest border border-border-subtle text-sm focus:outline-none focus:ring-2 focus:ring-electric-violet/30"
            placeholder="Audit note (e.g. beta onboarding)"
          />
          {form.formState.errors.reason && (
            <p className="text-xs text-red-300 mt-1">{form.formState.errors.reason.message}</p>
          )}
        </div>
        {mutation.error && (
          <p className="text-xs text-red-300">{(mutation.error as Error).message}</p>
        )}
      </form>
    </ConfirmModal>
  );
}
